import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Home as HomeIcon, Phone, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StickyCall } from "../components/StickyCall";
import { AccessibleField } from "../components/a11y/AccessibleField";
import { PHONE, PHONE_HREF, EMAIL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REVIEW_DATE = "September 4, 2026";

export default function AccessibilityPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.message.trim()) errs.message = "Please describe the accessibility issue so we can help.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email address, or leave it blank.";
    setErrors(errs);
    if (Object.keys(errs).length) {
      setStatus("Please fix the errors below and try again.");
      const firstId = errs.message ? "a11y-message" : "a11y-email";
      setTimeout(() => { document.querySelector(`[data-testid='${firstId}']`)?.focus(); }, 30);
      return;
    }
    setLoading(true); setStatus("Sending your feedback…");
    try {
      const payload = { message: form.message.trim(), page_url: window.location.href };
      if (form.name.trim()) payload.name = form.name.trim();
      if (form.email.trim()) payload.email = form.email.trim();
      await axios.post(`${API}/accessibility-feedback`, payload);
      setDone(true); setStatus("");
    } catch (err) {
      setStatus("Sorry, something went wrong. Please email us directly at " + EMAIL + " or call " + PHONE + ".");
    } finally { setLoading(false); }
  };

  const sections = [
    { h: "Our commitment", p: "Urgent Passport & Visa (UPV Services) is committed to providing a website that is accessible to the widest possible audience, regardless of ability or technology. We are actively working to make our digital experience easier to use for people with visual, auditory, physical, cognitive, and neurological disabilities." },
    { h: "Standard we aim for", p: "We use the Web Content Accessibility Guidelines (WCAG) 2.2 Level AA as our technical target. These internationally recognized guidelines help make web content more perceivable, operable, understandable, and robust for everyone, including people who use screen readers, keyboard navigation, screen magnification, and other assistive technologies." },
    { h: "What we have done", p: "Accessibility has been built into the website itself — not added as an overlay. This includes semantic HTML structure and landmarks, a visible \"skip to main content\" link, keyboard access to menus, forms, and interactive controls, clearly visible keyboard focus indicators, descriptive text alternatives for meaningful images, labelled form fields with accessible error messages, support for browser zoom and increased text spacing, and respect for the \"reduce motion\" system setting." },
    { h: "Ongoing effort", p: "Accessibility is an ongoing process, not a one-time project. We continue to review and improve the site over time, and we test using both automated tools and manual keyboard and screen-reader checks. Some content provided by third parties (for example, embedded maps and the payment card fields) may have limitations that are outside our direct control; where that is the case, we offer alternative ways to complete your request, including by phone." },
  ];

  return (
    <div data-testid="accessibility-page">
      <Seo
        title="Accessibility Statement | Urgent Passport & Visa (UPV Services)"
        description="Urgent Passport & Visa is committed to digital accessibility and works toward WCAG 2.2 Level AA. Report an accessibility barrier or request assistance."
        path="/accessibility"
      />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <section data-a11y-dark className="relative bg-[#0b192c] text-white">
          <div className="noise-overlay" />
          <div className="relative max-w-4xl mx-auto px-6 md:px-8 pt-14 pb-16 md:pt-20 md:pb-20">
            <nav className="flex items-center gap-2 text-sm text-white/70 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white inline-flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" aria-hidden="true" /> Home</Link>
              <span aria-hidden="true">/</span>
              <span className="text-white/90" aria-current="page">Accessibility</span>
            </nav>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]">Accessibility</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl leading-tight tracking-tight">Accessibility Statement</h1>
            <p className="mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">We want everyone to be able to use upvservices.com. Here's our commitment, the standard we follow, and how to reach us if you hit a barrier.</p>
            <p className="mt-4 text-sm text-white/55">Last accessibility review: {REVIEW_DATE}</p>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-6 md:px-8">
            <div className="space-y-10">
              {sections.map((s, i) => (
                <article key={i}>
                  <h2 className="font-display font-bold text-2xl text-[#0b192c] tracking-tight">{s.h}</h2>
                  <p className="mt-3 text-[17px] text-slate-600 leading-relaxed">{s.p}</p>
                </article>
              ))}

              <article>
                <h2 className="font-display font-bold text-2xl text-[#0b192c] tracking-tight">Contact us for help or to report a barrier</h2>
                <p className="mt-3 text-[17px] text-slate-600 leading-relaxed">
                  Encounter an accessibility barrier, or need information or a service in a different format? Please let us know — we welcome your feedback and will work to provide the information or service you need. You can reach us directly:
                </p>
                <ul className="mt-4 space-y-2 text-[16px] text-[#0b192c]">
                  <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#d90429]" aria-hidden="true" /> <a href={PHONE_HREF} className="font-semibold hover:text-[#d90429] transition-colors">{PHONE}</a></li>
                  <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#d90429]" aria-hidden="true" /> <a href={`mailto:${EMAIL}`} className="font-semibold hover:text-[#d90429] transition-colors">{EMAIL}</a></li>
                </ul>
              </article>

              {/* Feedback form */}
              <article className="rounded-2xl border border-slate-200 bg-[#f8f9fa] p-6 md:p-8">
                <h2 className="font-display font-bold text-2xl text-[#0b192c] tracking-tight">Send accessibility feedback</h2>
                {done ? (
                  <div className="mt-5 flex items-start gap-3 rounded-xl bg-white border border-slate-200 p-5" role="status" data-testid="a11y-feedback-success">
                    <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" aria-hidden="true" />
                    <p className="text-[16px] text-[#0b192c]">Thank you — your feedback has been sent. If you asked us to follow up, we'll be in touch shortly. You can also call {PHONE} anytime.</p>
                  </div>
                ) : (
                  <form className="mt-5 space-y-4" onSubmit={submit} noValidate data-testid="a11y-feedback-form">
                    <p className="text-sm text-slate-600">Only a message is required. Add your email if you'd like us to reply.</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <AccessibleField label="Your name" value={form.name} onChange={set("name")} autoComplete="name" data-testid="a11y-name" />
                      <AccessibleField label="Email" type="email" value={form.email} onChange={set("email")} error={errors.email} autoComplete="email" hint="Optional — only if you'd like a reply." data-testid="a11y-email" />
                    </div>
                    <AccessibleField as="textarea" label="How can we help?" required rows={5} value={form.message} onChange={set("message")} error={errors.message} hint="Please describe the barrier and, if you can, the page it happened on." data-testid="a11y-message" />
                    <p className="sr-only" role="status" aria-live="polite">{status}</p>
                    {status && !loading && <p className="text-sm font-medium text-[#b00020]" aria-hidden="true">{status}</p>}
                    <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#d90429] text-white px-7 py-3.5 font-semibold hover:bg-[#ef233c] transition-colors disabled:opacity-60" data-testid="a11y-submit">
                      {loading ? <><Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Sending…</> : "Send feedback"}
                    </button>
                  </form>
                )}
              </article>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
